import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Bell,
  Shield,
  User,
  Star,
  MessageSquare,
  ClipboardList,
  Copy,
  Check,
  ExternalLink,
  Upload,
  Trash2,
  Loader2,
} from 'lucide-react';
import { PageLayout } from '../../components/styled/PageLayout';
import { Container } from '../../components/styled/Container';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { useDispatch, useSelector } from 'react-redux';
import { changeMyPasswordThunk, clearAuthMessages, updateMyProfile } from '../../store/reducers/authReducer';
import { fetchMyActivity } from '../../store/actions/activityActions';
import { useI18n } from '../../hooks/useI18n';
import { fileToAvatarDataUrl } from '../../utils/avatarImage';

const DashboardWrap = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled(motion.div)`
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 800;
  margin: 0.5rem 0 0;
`;

const Sub = styled.p`
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 52rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: 1.25rem 0 1rem;
  width: 100%;
  @media (max-width: 640px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 0.35rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
`;

const TabBtn = styled.button`
  border: none;
  cursor: pointer;
  padding: 0.55rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  flex-shrink: 0;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.muted)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primaryForeground : theme.colors.foreground)};
  box-shadow: ${({ $active }) => ($active ? '0 2px 8px rgba(0,48,135,0.2)' : 'none')};
`;

const TabContent = styled.div`
  width: 100%;
  min-height: min(72vh, 900px);
  display: flex;
  flex-direction: column;
`;

/** Fills horizontal space: responsive columns */
const WideGrid = styled.div`
  display: grid;
  width: 100%;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  align-items: start;
  @media (min-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const WideGridDense = styled.div`
  display: grid;
  width: 100%;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  align-items: stretch;
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
  width: 100%;
  min-width: 0;
`;

const Accent = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
`;

const CardInner = styled.div`
  padding: 1.35rem 1.5rem 1.5rem;
  @media (min-width: 768px) {
    padding: 1.5rem 1.75rem 1.75rem;
  }
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Muted = styled.p`
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const StatBox = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.div`
  margin-top: 0.25rem;
  font-weight: 900;
  font-size: ${({ theme }) => theme.fontClamp.lg};
`;

const List = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Item = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`;

const ItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: baseline;
`;

const ItemTitle = styled.div`
  font-weight: 800;
`;

const ItemMeta = styled.div`
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const ItemText = styled.div`
  margin-top: 0.35rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  line-height: 1.5;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  cursor: pointer;
  user-select: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Checkbox = styled.input`
  width: 1.2rem;
  height: 1.2rem;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin-bottom: 0.35rem;
  letter-spacing: 0.02em;
`;

const Helper = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.4;
`;

const Input = styled.input`
  width: 100%;
  min-height: 2.85rem;
  padding: 0.55rem 0.85rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder {
    color: ${({ theme }) => theme.colors.mutedForeground};
    opacity: 0.85;
  }
  &:hover {
    border-color: rgba(0, 48, 135, 0.18);
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 48, 135, 0.12);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 7rem;
  padding: 0.65rem 0.85rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder {
    color: ${({ theme }) => theme.colors.mutedForeground};
    opacity: 0.85;
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 48, 135, 0.12);
  }
`;

const Field = styled.div`
  margin-bottom: 1rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 0 1rem;
  grid-template-columns: 1fr;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FormGridFullRow = styled.div`
  grid-column: 1 / -1;
`;

const BtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 2.85rem;
  padding: 0.6rem 1.35rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  cursor: pointer;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  &:hover:not(:disabled) {
    opacity: 0.94;
    filter: brightness(1.03);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const BtnSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 2.5rem;
  padding: 0.45rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  cursor: pointer;
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.foreground};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background};
  }
`;

const BtnDangerOutline = styled(BtnSecondary)`
  color: ${({ theme }) => theme.colors.crimson};
  border-color: rgba(220, 38, 38, 0.25);
`;

const AvatarBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const AvatarPreview = styled.div`
  width: 6.5rem;
  height: 6.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.muted};
  border: 3px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AvatarActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
`;

const HiddenFile = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const Message = styled.p`
  margin: 0.75rem 0 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ErrorText = styled(Message)`
  color: ${({ theme }) => theme.colors.crimson};
`;

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export default function DashboardPage() {
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const activity = useSelector((s) => s.activity);
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const { t } = useI18n();
  const fileInputRef = useRef(null);

  const homeTo = pathWithRegion('/', region);
  const loginTo = pathWithRegion('/login', region);

  const [tab, setTab] = useState('overview');
  const [profileForm, setProfileForm] = useState(() => ({
    username: auth.user?.username || '',
    displayName: auth.user?.displayName || '',
    locale: auth.user?.locale || 'en-US',
    timezone: auth.user?.timezone || '',
    bio: auth.user?.bio || '',
  }));
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '' });
  const [pendingAvatarDataUrl, setPendingAvatarDataUrl] = useState(null);
  const [avatarRemoved, setAvatarRemoved] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarErr, setAvatarErr] = useState('');

  const localVotes = useMemo(() => readJson('simimovies.activity.votes.v1', []), []);
  const localSuggestions = useMemo(
    () => readJson('simimovies.activity.suggestions.v1', []),
    [],
  );
  const localReviews = useMemo(() => readJson('simimovies.activity.reviews.v1', []), []);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    dispatch(fetchMyActivity());
  }, [dispatch, auth.isAuthenticated]);

  const votes = activity.loaded ? (activity.votes?.length ? activity.votes : localVotes) : localVotes;
  const suggestions = activity.loaded
    ? activity.suggestions?.length
      ? activity.suggestions
      : localSuggestions
    : localSuggestions;
  const reviews = activity.loaded ? (activity.reviews?.length ? activity.reviews : localReviews) : localReviews;

  const timeline = useMemo(() => {
    const norm = (type, x) => ({ type, ...x });
    return [
      ...votes.map((x) => norm('vote', x)),
      ...suggestions.map((x) => norm('suggestion', x)),
      ...reviews.map((x) => norm('review', x)),
    ].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [votes, suggestions, reviews]);

  const stats = useMemo(() => {
    const avgRating =
      votes.length === 0 ? null : votes.reduce((sum, v) => sum + (Number(v.rating) || 0), 0) / votes.length;
    return {
      votes: votes.length,
      suggestions: suggestions.length,
      reviews: reviews.length,
      avgRating: avgRating != null ? avgRating.toFixed(1) : '—',
    };
  }, [votes, suggestions, reviews]);

  const [prefs, setPrefs] = useState(() => ({
    emailNotifs: auth.user?.settings?.emailNotifs !== false,
    contentTips: auth.user?.settings?.contentTips !== false,
    publicProfile: auth.user?.settings?.publicProfile === true,
  }));

  useEffect(() => {
    if (!auth.user) return;
    setProfileForm({
      username: auth.user.username || '',
      displayName: auth.user.displayName || '',
      locale: auth.user.locale || 'en-US',
      timezone: auth.user.timezone || '',
      bio: auth.user.bio || '',
    });
    setPrefs({
      emailNotifs: auth.user.settings?.emailNotifs !== false,
      contentTips: auth.user.settings?.contentTips !== false,
      publicProfile: auth.user.settings?.publicProfile === true,
    });
    setPendingAvatarDataUrl(null);
    setAvatarRemoved(false);
    setAvatarErr('');
  }, [auth.user]);

  if (!auth.isAuthenticated) {
    return <Navigate to={loginTo} replace />;
  }

  const savePrefs = (next) => {
    setPrefs(next);
    dispatch(clearAuthMessages());
    dispatch(
      updateMyProfile({
        settings: {
          emailNotifs: next.emailNotifs,
          contentTips: next.contentTips,
          publicProfile: next.publicProfile,
        },
      }),
    );
  };

  const profilePublicUrl =
    typeof window !== 'undefined' && auth.user?.username
      ? `${window.location.origin}${pathWithRegion(`/profile/${auth.user.username}`, region)}`
      : '';

  const displayAvatarSrc = avatarRemoved ? null : pendingAvatarDataUrl || auth.user?.avatarUrl || null;

  const showAvatarPlaceholder = !displayAvatarSrc;

  const onPickAvatar = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarErr('');
    setAvatarBusy(true);
    try {
      const dataUrl = await fileToAvatarDataUrl(file);
      setPendingAvatarDataUrl(dataUrl);
      setAvatarRemoved(false);
    } catch (err) {
      setAvatarErr(err?.message || 'Could not use that image.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveProfile = () => {
    dispatch(clearAuthMessages());
    const payload = {
      displayName: profileForm.displayName,
      locale: profileForm.locale,
      timezone: profileForm.timezone,
      username: profileForm.username || undefined,
      bio: profileForm.bio,
      settings: {
        emailNotifs: prefs.emailNotifs,
        contentTips: prefs.contentTips,
        publicProfile: prefs.publicProfile,
      },
    };
    if (pendingAvatarDataUrl) {
      payload.avatarDataUrl = pendingAvatarDataUrl;
    } else if (avatarRemoved) {
      payload.avatarUrl = '';
    }
    dispatch(updateMyProfile(payload))
      .unwrap()
      .then(() => {
        setPendingAvatarDataUrl(null);
        setAvatarRemoved(false);
      })
      .catch(() => {});
  };

  return (
    <PageLayout>
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem', width: '100%', maxWidth: '100%' }}>
        <DashboardWrap>
          <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <BackLink to={homeTo}>
              <ArrowLeft size={16} strokeWidth={2.5} /> Home
            </BackLink>
            <Title>{t('nav.dashboard', 'Dashboard')}</Title>
            <Sub>
              {t(
                'dashboard.subtitle',
                'Your contributions across similar picks: votes, suggestions, and reviews.',
              )}
            </Sub>
          </Header>

          <Tabs>
            <TabBtn type="button" $active={tab === 'overview'} onClick={() => setTab('overview')}>
              {t('dashboard.tab.overview', 'Overview')}
            </TabBtn>
            <TabBtn type="button" $active={tab === 'history'} onClick={() => setTab('history')}>
              {t('dashboard.tab.history', 'History')}
            </TabBtn>
            <TabBtn type="button" $active={tab === 'timeline'} onClick={() => setTab('timeline')}>
              {t('dashboard.tab.timeline', 'Timeline')}
            </TabBtn>
            <TabBtn type="button" $active={tab === 'prefs'} onClick={() => setTab('prefs')}>
              {t('dashboard.tab.preferences', 'Preferences')}
            </TabBtn>
            <TabBtn type="button" $active={tab === 'security'} onClick={() => setTab('security')}>
              {t('dashboard.tab.security', 'Security')}
            </TabBtn>
          </Tabs>

          <TabContent>
            {tab === 'overview' && (
              <WideGridDense>
                <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Accent />
                  <CardInner>
                    <CardTitle>
                      <User size={18} /> {t('dashboard.myAccount', 'My account')}
                    </CardTitle>
                    <Muted>
                      <strong>{auth.user?.displayName || t('dashboard.user', 'User')}</strong>
                      <br />
                      {auth.user?.username ? (
                        <>
                          @{auth.user.username}
                          <br />
                        </>
                      ) : null}
                      {auth.user?.email}
                    </Muted>
                    {auth.user?.username && prefs.publicProfile ? (
                      <div style={{ marginTop: '0.85rem' }}>
                        <Link to={pathWithRegion(`/profile/${auth.user.username}`, region)}>
                          <ExternalLink size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                          View public profile
                        </Link>
                      </div>
                    ) : null}
                    <StatRow>
                      <StatBox>
                        <StatLabel>{t('common.votes', 'Votes')}</StatLabel>
                        <StatValue>{stats.votes}</StatValue>
                      </StatBox>
                      <StatBox>
                        <StatLabel>{t('dashboard.suggestions', 'Suggestions')}</StatLabel>
                        <StatValue>{stats.suggestions}</StatValue>
                      </StatBox>
                      <StatBox>
                        <StatLabel>{t('common.reviews', 'Reviews')}</StatLabel>
                        <StatValue>{stats.reviews}</StatValue>
                      </StatBox>
                    </StatRow>
                    <StatRow style={{ gridTemplateColumns: '1fr', marginTop: '0.75rem' }}>
                      <StatBox>
                        <StatLabel>{t('dashboard.avgSimilarityRating', 'Avg similarity rating')}</StatLabel>
                        <StatValue>{stats.avgRating}/5</StatValue>
                      </StatBox>
                    </StatRow>
                  </CardInner>
                </Card>

                <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Accent />
                  <CardInner>
                    <CardTitle>
                      <ClipboardList size={18} /> {t('dashboard.quickActions', 'Quick actions')}
                    </CardTitle>
                    <Muted>{t('dashboard.quickActionsSub', 'Jump back into browsing and contribute more community data.')}</Muted>
                    <List>
                      <Item>
                        <ItemTop>
                          <ItemTitle>{t('dashboard.findSimilarPicks', 'Find similar picks')}</ItemTitle>
                          <ItemMeta>{t('explore.browse', 'Browse')}</ItemMeta>
                        </ItemTop>
                        <ItemText>
                          {t('dashboard.findSimilarCopyPrefix', 'Go to')}{' '}
                          <Link to={pathWithRegion('/similar/movies', region)}>{t('nav.similarMovies', 'Similar movies')}</Link>{' '}
                          {t('dashboard.findSimilarCopySuffix', 'or search any title to vote.')}
                        </ItemText>
                      </Item>
                      <Item>
                        <ItemTop>
                          <ItemTitle>{t('dashboard.browseCategories', 'Browse categories')}</ItemTitle>
                          <ItemMeta>{t('dashboard.explore', 'Explore')}</ItemMeta>
                        </ItemTop>
                        <ItemText>
                          {t('dashboard.openCategoriesPrefix', 'Open')}{' '}
                          <Link to={pathWithRegion('/categories', region)}>{t('nav.categories', 'Categories')}</Link>{' '}
                          {t('dashboard.openCategoriesSuffix', 'to discover titles by genre.')}
                        </ItemText>
                      </Item>
                    </List>
                  </CardInner>
                </Card>
              </WideGridDense>
            )}

            {tab === 'history' && (
              <WideGridDense>
                <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Accent />
                  <CardInner>
                    <CardTitle>
                      <Star size={18} /> {t('dashboard.similarityVotes', 'Similarity votes')}
                    </CardTitle>
                    <Muted>{t('dashboard.similarityVotesSub', 'Ratings you gave on similar pages (1-5 stars), including your reason when provided.')}</Muted>
                    <List>
                      {votes.length === 0 ? (
                        <Muted>{t('common.noVotes', 'No votes yet.')}</Muted>
                      ) : (
                        votes.slice(0, 24).map((v) => (
                          <Item key={v.id}>
                            <ItemTop>
                              <ItemTitle>
                                {v.baseTitle} → {v.altTitle}
                              </ItemTitle>
                              <ItemMeta>{new Date(v.createdAt).toLocaleString()}</ItemMeta>
                            </ItemTop>
                            <ItemText>
                              {t('dashboard.rating', 'Rating')}: <strong>{v.rating}/5</strong>
                              {v.reason ? ` · ${v.reason}` : ''}
                            </ItemText>
                          </Item>
                        ))
                      )}
                    </List>
                  </CardInner>
                </Card>

                <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Accent />
                  <CardInner>
                    <CardTitle>
                      <MessageSquare size={18} /> {t('dashboard.suggestionsReviews', 'Suggestions & reviews')}
                    </CardTitle>
                    <Muted>{t('dashboard.suggestionsReviewsSub', 'Your submitted suggestions and reviews.')}</Muted>
                    <List>
                      {suggestions.slice(0, 12).map((s) => (
                        <Item key={s.id}>
                          <ItemTop>
                            <ItemTitle>
                              {t('dashboard.suggested', 'Suggested')}: {s.suggestedTitle}
                            </ItemTitle>
                            <ItemMeta>
                              {s.status || t('dashboard.pending', 'pending')} · {new Date(s.createdAt).toLocaleString()}
                            </ItemMeta>
                          </ItemTop>
                          <ItemText>
                            {t('dashboard.for', 'For')}: <strong>{s.baseTitle}</strong>
                            {s.reason ? ` · ${s.reason}` : ''}
                          </ItemText>
                        </Item>
                      ))}
                      {reviews.slice(0, 12).map((r) => (
                        <Item key={r.id}>
                          <ItemTop>
                            <ItemTitle>
                              {t('dashboard.review', 'Review')}: {r.title}
                            </ItemTitle>
                            <ItemMeta>{new Date(r.createdAt).toLocaleString()}</ItemMeta>
                          </ItemTop>
                          <ItemText>
                            {t('dashboard.rating', 'Rating')}: <strong>{r.rating}/5</strong>
                            {r.text ? ` · ${r.text}` : ''}
                          </ItemText>
                        </Item>
                      ))}
                      {suggestions.length === 0 && reviews.length === 0 && (
                        <Muted>{t('dashboard.noSuggestionsReviews', 'No suggestions or reviews yet.')}</Muted>
                      )}
                    </List>
                  </CardInner>
                </Card>
              </WideGridDense>
            )}

            {tab === 'timeline' && (
              <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ flex: 1 }}>
                <Accent />
                <CardInner>
                  <CardTitle>
                    <ClipboardList size={18} /> Activity timeline
                  </CardTitle>
                  <Muted>Combined view of votes, suggestions, and reviews in chronological order.</Muted>
                  <List>
                    {timeline.length === 0 ? (
                      <Muted>No activity yet.</Muted>
                    ) : (
                      timeline.slice(0, 48).map((x) => (
                        <Item key={`${x.type}-${x.id}`}>
                          <ItemTop>
                            <ItemTitle>
                              {x.type === 'vote' && `Voted ${x.rating}/5`}
                              {x.type === 'suggestion' && `Suggested`}
                              {x.type === 'review' && `Reviewed ${x.rating}/5`}
                            </ItemTitle>
                            <ItemMeta>{x.createdAt ? new Date(x.createdAt).toLocaleString() : ''}</ItemMeta>
                          </ItemTop>
                          <ItemText>
                            {x.type === 'vote' && `${x.baseTitle} → ${x.altTitle}${x.reason ? ` · ${x.reason}` : ''}`}
                            {x.type === 'suggestion' &&
                              `${x.baseTitle} · ${x.suggestedTitle}${x.reason ? ` · ${x.reason}` : ''}`}
                            {x.type === 'review' && `${x.title}${x.text ? ` · ${x.text}` : ''}`}
                          </ItemText>
                        </Item>
                      ))
                    )}
                  </List>
                </CardInner>
              </Card>
            )}

            {tab === 'prefs' && (
              <WideGrid>
                <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Accent />
                  <CardInner>
                    <CardTitle>
                      <User size={18} /> Profile
                    </CardTitle>
                    <Muted>Username, photo, and bio used on your public page when enabled.</Muted>

                    <AvatarBlock>
                      <AvatarPreview>
                        {showAvatarPlaceholder ? (
                          <User size={40} strokeWidth={1.5} style={{ opacity: 0.35 }} />
                        ) : (
                          <img src={displayAvatarSrc} alt="" />
                        )}
                      </AvatarPreview>
                      <AvatarActions>
                        <HiddenFile ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onPickAvatar} />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <BtnSecondary type="button" disabled={avatarBusy} onClick={() => fileInputRef.current?.click()}>
                            {avatarBusy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                            {avatarBusy ? 'Processing…' : 'Upload photo'}
                          </BtnSecondary>
                          {(auth.user?.avatarUrl || pendingAvatarDataUrl) && !avatarRemoved ? (
                            <BtnDangerOutline
                              type="button"
                              onClick={() => {
                                setPendingAvatarDataUrl(null);
                                setAvatarRemoved(true);
                                setAvatarErr('');
                              }}
                            >
                              <Trash2 size={16} /> Remove
                            </BtnDangerOutline>
                          ) : null}
                        </div>
                        <Helper>JPG, PNG, WebP, or GIF. Images are resized automatically before upload.</Helper>
                        {avatarErr ? <ErrorText style={{ marginTop: '0.5rem' }}>{avatarErr}</ErrorText> : null}
                      </AvatarActions>
                    </AvatarBlock>

                    <FormGrid>
                      <Field>
                        <Label htmlFor="dash-username">Username</Label>
                        <Input
                          id="dash-username"
                          value={profileForm.username}
                          onChange={(e) =>
                            setProfileForm((x) => ({
                              ...x,
                              username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''),
                            }))
                          }
                          placeholder="your_handle"
                          autoComplete="username"
                        />
                        <Helper>3–30 characters: letters, numbers, underscores. Required for a public profile URL.</Helper>
                      </Field>
                      <Field>
                        <Label htmlFor="dash-display">Display name</Label>
                        <Input
                          id="dash-display"
                          value={profileForm.displayName}
                          onChange={(e) => setProfileForm((x) => ({ ...x, displayName: e.target.value }))}
                          placeholder="How your name appears"
                          autoComplete="name"
                        />
                      </Field>
                      <Field>
                        <Label htmlFor="dash-locale">Locale</Label>
                        <Input
                          id="dash-locale"
                          value={profileForm.locale}
                          onChange={(e) => setProfileForm((x) => ({ ...x, locale: e.target.value }))}
                          placeholder="en-US"
                        />
                      </Field>
                      <Field>
                        <Label htmlFor="dash-tz">Timezone</Label>
                        <Input
                          id="dash-tz"
                          value={profileForm.timezone}
                          onChange={(e) => setProfileForm((x) => ({ ...x, timezone: e.target.value }))}
                          placeholder="e.g. America/New_York"
                        />
                      </Field>
                      <FormGridFullRow>
                        <Field>
                          <Label htmlFor="dash-bio">Bio</Label>
                          <TextArea
                            id="dash-bio"
                            value={profileForm.bio}
                            onChange={(e) => setProfileForm((x) => ({ ...x, bio: e.target.value.slice(0, 500) }))}
                            placeholder="Short bio for your public profile"
                            rows={5}
                          />
                          <Helper>{profileForm.bio.length}/500</Helper>
                        </Field>
                      </FormGridFullRow>
                    </FormGrid>

                    {profilePublicUrl && prefs.publicProfile ? (
                      <div style={{ marginTop: '1rem' }}>
                        <BtnSecondary
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(profilePublicUrl);
                            setCopiedUrl(true);
                            setTimeout(() => setCopiedUrl(false), 2000);
                          }}
                        >
                          {copiedUrl ? <Check size={16} /> : <Copy size={16} />}
                          {copiedUrl ? 'Link copied' : 'Copy profile link'}
                        </BtnSecondary>
                      </div>
                    ) : null}

                    <div style={{ marginTop: '1.25rem' }}>
                      <BtnPrimary type="button" disabled={auth.loading} onClick={saveProfile}>
                        {auth.loading ? <Loader2 size={18} className="spin" /> : null}
                        Save profile
                      </BtnPrimary>
                      {auth.profileMessage ? <Message>{auth.profileMessage}</Message> : null}
                      {auth.error ? <ErrorText>{auth.error}</ErrorText> : null}
                    </div>
                  </CardInner>
                </Card>

                <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Accent />
                  <CardInner>
                    <CardTitle>
                      <Bell size={18} /> Notifications & privacy
                    </CardTitle>
                    <Muted>These settings save to your account when toggled.</Muted>
                    <div style={{ marginTop: '1rem', display: 'grid', gap: '0.65rem' }}>
                      <ToggleRow>
                        <span>Email notifications</span>
                        <Checkbox
                          type="checkbox"
                          checked={!!prefs.emailNotifs}
                          onChange={(e) => savePrefs({ ...prefs, emailNotifs: e.target.checked })}
                        />
                      </ToggleRow>
                      <ToggleRow>
                        <span>Content tips & onboarding</span>
                        <Checkbox
                          type="checkbox"
                          checked={!!prefs.contentTips}
                          onChange={(e) => savePrefs({ ...prefs, contentTips: e.target.checked })}
                        />
                      </ToggleRow>
                      <ToggleRow>
                        <span>Public profile (others can see your activity)</span>
                        <Checkbox
                          type="checkbox"
                          checked={!!prefs.publicProfile}
                          onChange={(e) => savePrefs({ ...prefs, publicProfile: e.target.checked })}
                        />
                      </ToggleRow>
                    </div>
                  </CardInner>
                </Card>
              </WideGrid>
            )}

            {tab === 'security' && (
              <Card initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', flex: 1 }}>
                <Accent />
                <CardInner>
                  <CardTitle>
                    <Shield size={18} /> Security
                  </CardTitle>
                  <Muted>Update your password or use the forgot-password flow.</Muted>
                  <List style={{ marginTop: '1rem' }}>
                    <Item>
                      <ItemTop>
                        <ItemTitle>Change password</ItemTitle>
                        <ItemMeta>Account</ItemMeta>
                      </ItemTop>
                      <ItemText>
                        <FormGrid style={{ marginTop: '0.75rem' }}>
                          <Field>
                            <Label htmlFor="pw-current">Current password</Label>
                            <Input
                              id="pw-current"
                              type="password"
                              autoComplete="current-password"
                              placeholder="••••••••"
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm((x) => ({ ...x, currentPassword: e.target.value }))}
                            />
                          </Field>
                          <Field>
                            <Label htmlFor="pw-new">New password</Label>
                            <Input
                              id="pw-new"
                              type="password"
                              autoComplete="new-password"
                              placeholder="At least 6 characters"
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm((x) => ({ ...x, newPassword: e.target.value }))}
                            />
                          </Field>
                          <FormGridFullRow>
                            <BtnPrimary
                              type="button"
                              disabled={auth.loading}
                              onClick={() => {
                                dispatch(clearAuthMessages());
                                dispatch(changeMyPasswordThunk(passwordForm))
                                  .unwrap()
                                  .then(() => setPasswordForm({ currentPassword: '', newPassword: '' }));
                              }}
                            >
                              Change password
                            </BtnPrimary>
                          </FormGridFullRow>
                        </FormGrid>
                        <div style={{ marginTop: '0.75rem' }}>
                          <Link to={pathWithRegion('/forgot-password', region)}>Forgot password?</Link>
                        </div>
                        {auth.profileMessage ? <Message>{auth.profileMessage}</Message> : null}
                        {auth.error ? <ErrorText>{auth.error}</ErrorText> : null}
                      </ItemText>
                    </Item>
                    <Item>
                      <ItemTop>
                        <ItemTitle>Session</ItemTitle>
                        <ItemMeta>Sign out</ItemMeta>
                      </ItemTop>
                      <ItemText>Use the account menu in the navbar to log out.</ItemText>
                    </Item>
                  </List>
                </CardInner>
              </Card>
            )}
          </TabContent>
        </DashboardWrap>
      </Container>
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        .spin {
          animation: spin 0.8s linear infinite;
        }
      `}</style>
    </PageLayout>
  );
}
